// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Loader2, Calendar, MapPin, Tag, Camera, CheckCircle } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';

import recruitlookHoopsLogo from '@/assets/recruitlook-hoops-logo.jpeg';
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";
import { DEFAULT_MEDIA_FIELDS } from '@/types/events';
import { formatUsPhone, phoneDigits } from '@/lib/phone';


interface EventData {
  id: string;
  slug?: string;
  name: string;
  start_date: string;
  end_date?: string;
  venue_name?: string;
  venue_city?: string;
  venue_state?: string;
  media_credentials_description?: string;
  media_credentials_form_id?: string;
  media_credentials_price?: number;
  media_credentials_coupon_code?: string;
  media_credentials_coupon_discount_type?: string;
  media_credentials_coupon_discount_value?: number;
  media_credentials_notification_email?: string;
  enable_media_credentials?: boolean;
  media_credentials_enabled?: boolean;
}

interface FormField {
  id: string;
  field_name: string;
  field_type: string;
  field_label: string;
  is_required: boolean;
  field_options?: string[];
  placeholder?: string;
  sort_order?: number;
}

interface FormResponse {
  [key: string]: string;
}

interface MediaCredentialReceipt {
  request_id: string;
  reference: string;
  name: string;
  email: string;
  outlet: string;
  coverage_type: string;
  status: string;
  is_approved: boolean;
  is_denied: boolean;
  denial_reason: string;
  payment_status: string;
  is_paid: boolean;
  subtotal: string;
  processing_fee: string;
  has_processing_fee: boolean;
  total_paid: string;
  subtotal_cents: number;
  processing_fee_cents: number;
  total_paid_cents: number;
  paid_at: string;
  receipt_date: string;
  event_name: string;
  event_dates: string;
  media_guidelines_url: string;
  checkin_location: string;
}

const EventMediaCredentialsPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const eventId = searchParams.get('event_id');

  const paymentStatus = searchParams.get('payment');
  const paymentAmountCents = Number(searchParams.get('amount') || '0') || 0;
  const paymentSessionId = searchParams.get('session_id');
  const paymentCoupon = searchParams.get('coupon');
  
  const [loading, setLoading] = useState(true);
  const [event, setEvent] = useState<EventData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [formFields, setFormFields] = useState<FormField[]>([]);
  const [formData, setFormData] = useState<FormResponse>({});
  const [submitting, setSubmitting] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [couponApplied, setCouponApplied] = useState(false);
  // Prices are stored in DOLLARS on the event row.
  const [discountedPrice, setDiscountedPrice] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [submittedRequest, setSubmittedRequest] = useState<Record<string, any> | null>(
    (location.state as any)?.credential ?? null,
  );
  const [receipt, setReceipt] = useState<MediaCredentialReceipt | null>(null);
  const [receiptLoading, setReceiptLoading] = useState(false);
  const [receiptError, setReceiptError] = useState<string | null>(null);


  useEffect(() => {
    if (!eventId) {
      setError('No event ID provided');
      setLoading(false);
      return;
    }

    // After Stripe redirect, load event data, finalize the request server-side,
    // then load the receipt so the applicant sees exactly what they paid.
    if (paymentStatus === 'success' && paymentSessionId) {
      (async () => {
        await fetchEventData();

        try {
          const { error: finalizeError } = await invokeEdgeFunction('media-credentials-stripe-success', {
            body: { session_id: paymentSessionId },
          });
          if (finalizeError) throw finalizeError;
        } catch (err) {
          console.error('[MEDIA-CREDENTIALS] Failed to finalize paid submission:', err);
        }

        setReceiptLoading(true);
        try {
          const { data: receiptData, error: receiptError } = await invokeEdgeFunction('rle-media-credential-receipt', {
            body: { session_id: paymentSessionId },
          });
          if (receiptError) throw receiptError;
          if (receiptData?.ok && receiptData.data) {
            setReceipt(receiptData.data as MediaCredentialReceipt);
          } else {
            setReceiptError('Receipt details could not be loaded — please check your email.');
          }
        } catch (err) {
          console.error('[MEDIA-CREDENTIALS] Failed to load receipt:', err);
          setReceiptError('Receipt details could not be loaded — please check your email.');
        } finally {
          setReceiptLoading(false);
        }

        setSubmitted(true);
      })();
      return;
    }


    // Check if redirected with submitted=true
    if (searchParams.get('submitted') === 'true') {
      setSubmitted(true);
    }

    fetchEventData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  const fetchEventData = async () => {
    try {
      const { data, error: fetchError } = await supabase
        .from('events')
        .select(`
          id,
          slug,
          name,
          start_date,
          end_date,
          venue_name,
          venue_city,
          venue_state,
          media_credentials_description,
          media_credentials_form_id,
          media_credentials_price,
          media_credentials_paid,
          media_credentials_coupon_code,
          media_credentials_coupon_discount_type,
          media_credentials_coupon_discount_value,
          media_credentials_notification_email,
          enable_media_credentials,
          media_credentials_enabled
        `)
        .eq('id', eventId)
        .single();

      if (fetchError) {
        console.error('Error fetching event:', fetchError);
        setError('Event not found');
        setLoading(false);
        return;
      }

      setEvent(data);
      // Only charge when the paid switch is on, whatever price is configured.
      setDiscountedPrice(data.media_credentials_paid ? Number(data.media_credentials_price) || 0 : 0);

      // Fetch form fields if form exists
      let loadedFields: FormField[] = [];
      if (data.media_credentials_form_id) {
        const { data: formData, error: formError } = await supabase
          .from('event_registration_forms')
          .select('fields')
          .eq('id', data.media_credentials_form_id)
          .maybeSingle();

        if (!formError && formData?.fields) {
          loadedFields = (formData.fields as any[]).map((field: any, index: number) => ({
            id: field.id?.toString() || index.toString(),
            field_name: field.label.toLowerCase().replace(/[^a-z0-9]/g, '_'),
            field_type: field.type,
            field_label: field.label,
            is_required: field.required || false,
            field_options: field.options || [],
            placeholder: field.placeholder || '',
            sort_order: index
          }));
        }
      }

      // No configured form is not a dead end — fall back to the standard fields
      // so an enabled event always accepts requests.
      if (!loadedFields.length) {
        loadedFields = (DEFAULT_MEDIA_FIELDS as any[]).map((field: any, index: number) => ({
          id: String(field.id ?? field.key ?? index),
          field_name: String(field.key ?? field.label ?? index).toLowerCase().replace(/[^a-z0-9]/g, '_'),
          field_type: field.type || 'text',
          field_label: field.label,
          is_required: field.required || false,
          field_options: field.options || [],
          placeholder: field.placeholder || '',
          sort_order: index,
        }));
      }
      setFormFields(loadedFields);

    } catch (err) {
      console.error('Error:', err);
      setError('An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (fieldName: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  const handleApplyCoupon = async () => {
    if (!couponCode.trim() || !event) return;

    if (event.media_credentials_coupon_code && 
        couponCode.toUpperCase() === event.media_credentials_coupon_code.toUpperCase()) {
      setCouponApplied(true);
      
      // Prices and fixed discounts are both in DOLLARS.
      const basePrice = event.media_credentials_paid ? Number(event.media_credentials_price) || 0 : 0;
      const discountType = event.media_credentials_coupon_discount_type || 'percentage';
      const discountValue = Number(event.media_credentials_coupon_discount_value) || 100;

      let newPrice = basePrice;
      if (discountType === 'percentage') {
        newPrice = Math.max(0, basePrice * (1 - discountValue / 100));
      } else {
        newPrice = Math.max(0, basePrice - discountValue);
      }

      setDiscountedPrice(newPrice);

      if (newPrice === 0) {
        toast.success('Coupon applied! Media credentials are now free.');
      } else {
        toast.success(`Coupon applied! You save $${(basePrice - newPrice).toFixed(2)}.`);
      }

    } else {
      toast.error('Invalid coupon code');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const requiredFields = formFields.filter(field => field.is_required);
    for (const field of requiredFields) {
      if (!formData[field.field_name]?.trim()) {
        toast.error(`Please fill in ${field.field_label}`);
        return;
      }
    }

    setSubmitting(true);
    try {
      const nameField = Object.keys(formData).find(key => 
        key.includes('name') && !key.includes('organization')
      );
      const emailField = Object.keys(formData).find(key => key.includes('email'));
      const orgField = Object.keys(formData).find(key => 
        key.includes('organization') || key.includes('publication') || key.includes('company')
      );
      
      const requesterName = nameField ? formData[nameField] : '';
      const requesterEmail = emailField ? formData[emailField] : '';
      const requesterOrganization = orgField ? formData[orgField] : '';

      const phoneKey = Object.keys(formData).find((k) => /phone|cell|mobile/i.test(k));
      const coverageKey = Object.keys(formData).find((k) => /coverage|credential_type|media_type/i.test(k));

      const { data, error } = await invokeEdgeFunction('create-media-credentials-checkout', {
        body: {
          eventId,
          amount: discountedPrice,
          requesterEmail,
          requesterName,
          requesterOrganization,
          formData,
          couponCode: couponApplied ? couponCode : undefined,
        },
      });

      if (error) throw error;

      if (data?.url) {
        window.location.href = data.url;
        return;
      }

      if (data?.success) {
        setSubmittedRequest({
          name: requesterName,
          email: requesterEmail,
          organization: requesterOrganization,
          phone: phoneKey ? formData[phoneKey] : '',
          coverage_type: coverageKey ? formData[coverageKey] : '',
          ...(data.credential ?? {}),
        });
        setSubmitted(true);
        toast.success(data.message || 'Media credentials request submitted successfully!');
        return;
      }

      // A failed write must never present as success.
      throw new Error(data?.error || 'We could not submit your request. Please try again.');
    } catch (error: any) {
      console.error('Error submitting media credentials request:', error);
      toast.error(error?.message || 'Error submitting request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };


  const renderFormField = (field: FormField) => {
    const commonProps = {
      id: field.field_name,
      name: field.field_name,
      value: formData[field.field_name] || '',
      onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => 
        handleInputChange(field.field_name, e.target.value),
      required: field.is_required,
      placeholder: field.placeholder || '',
      className: "w-full"
    };

    switch (field.field_type) {
      case 'textarea':
        return <Textarea {...commonProps} rows={4} />;
      case 'email':
        return <Input {...commonProps} type="email" />;
      case 'phone':
      case 'tel':
        return (
          <Input
            id={field.field_name}
            name={field.field_name}
            type="tel"
            inputMode="tel"
            className="w-full"
            required={field.is_required}
            placeholder={field.placeholder || '(816) 223-7452'}
            value={formatUsPhone(formData[field.field_name] || '')}
            onChange={(e) => handleInputChange(field.field_name, phoneDigits(e.target.value))}
          />
        );

      case 'number':
        return <Input {...commonProps} type="number" />;
      case 'dropdown':
        return (
          <select {...commonProps} className="w-full px-3 py-2 border border-input bg-background rounded-md">
            <option value="">Select an option</option>
            {field.field_options?.map((option, index) => (
              <option key={index} value={option}>
                {option}
              </option>
            ))}
          </select>
        );
      default:
        return <Input {...commonProps} type="text" />;
    }
  };

  const formatEventDates = () => {
    if (!event?.start_date) return 'TBD';
    // Parse dates as local time to avoid timezone offset issues
    const [startYear, startMonth, startDay] = event.start_date.split('-').map(Number);
    const startDate = new Date(startYear, startMonth - 1, startDay);
    
    if (event.end_date) {
      const [endYear, endMonth, endDay] = event.end_date.split('-').map(Number);
      const endDate = new Date(endYear, endMonth - 1, endDay);
      return `${format(startDate, 'MMMM d')} - ${format(endDate, 'MMMM d, yyyy')}`;
    }
    return format(startDate, 'MMMM d, yyyy');
  };

  const getVenueLocation = () => {
    const parts = [event?.venue_name, event?.venue_city, event?.venue_state].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : 'TBD';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground">Loading event details...</p>
        </div>
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <p className="text-destructive mb-4">{error || 'Event not found'}</p>
            <Button onClick={() => navigate('/events')}>
              Back to Events
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Success State
  if (submitted) {
    // Paid credentials: show the real receipt when we have it.
    if (receipt) {
      const statusText = receipt.is_denied
        ? receipt.denial_reason || 'This request has been denied.'
        : receipt.is_approved
          ? 'Your media credential is approved.'
          : 'Your request is under review.';

      return (
        <div className="min-h-screen bg-background py-8 px-4">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-4">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <h1 className="text-2xl font-bold text-foreground mb-2">Payment Confirmed</h1>
              <p className="text-muted-foreground">
                Your media credentials request has been submitted and payment received.
              </p>
            </div>

            <Card className="mb-6">
              <CardContent className="pt-6">
                <h2 className="text-lg font-semibold mb-4 text-primary">Status</h2>
                <p className="text-sm">{statusText}</p>
              </CardContent>
            </Card>

            {receipt.is_paid && (
              <Card className="mb-6">
                <CardContent className="pt-6">
                  <h2 className="text-lg font-semibold mb-4 text-primary">Receipt</h2>
                  <dl className="space-y-3 text-sm">
                    {receipt.subtotal && (
                      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                        <dt className="text-muted-foreground">Media credential</dt>
                        <dd className="font-medium text-right">{receipt.subtotal}</dd>
                      </div>
                    )}
                    {receipt.has_processing_fee && receipt.processing_fee && (
                      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                        <dt className="text-muted-foreground">Card processing fee (3%)</dt>
                        <dd className="font-medium text-right">{receipt.processing_fee}</dd>
                      </div>
                    )}
                    {receipt.total_paid && (
                      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 border-t pt-3">
                        <dt className="font-semibold">Total paid</dt>
                        <dd className="font-bold text-base text-right">{receipt.total_paid}</dd>
                      </div>
                    )}
                    {receipt.receipt_date && (
                      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                        <dt className="text-muted-foreground">Paid on</dt>
                        <dd className="font-medium text-right">{receipt.receipt_date}</dd>
                      </div>
                    )}
                    {receipt.reference && (
                      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                        <dt className="text-muted-foreground">Reference</dt>
                        <dd className="font-medium text-right">{receipt.reference}</dd>
                      </div>
                    )}
                  </dl>
                  {receipt.reference && (
                    <p className="text-xs text-muted-foreground mt-4">
                      Have reference <strong>{receipt.reference}</strong> ready at check-in.
                    </p>
                  )}
                </CardContent>
              </Card>
            )}

            {(receipt.event_name || receipt.event_dates) && (
              <Card className="mb-6">
                <CardContent className="pt-6">
                  <h2 className="text-lg font-semibold mb-4 text-primary">Event</h2>
                  <div className="space-y-1 text-sm">
                    {receipt.event_name && <p className="font-medium">{receipt.event_name}</p>}
                    {receipt.event_dates && <p className="text-muted-foreground">{receipt.event_dates}</p>}
                  </div>
                </CardContent>
              </Card>
            )}

            {receipt.media_guidelines_url && (
              <Card className="mb-6">
                <CardContent className="pt-6">
                  <h2 className="text-lg font-semibold mb-4 text-primary">Media Guidelines</h2>
                  <a
                    href={receipt.media_guidelines_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center min-h-[44px] text-sm text-primary underline hover:text-primary/80"
                  >
                    View media guidelines
                  </a>
                </CardContent>
              </Card>
            )}

            {receipt.checkin_location && (
              <Card className="mb-6">
                <CardContent className="pt-6">
                  <h2 className="text-lg font-semibold mb-4 text-primary">Check-in Location</h2>
                  <div className="flex items-start gap-3 text-sm">
                    <MapPin className="h-5 w-5 text-muted-foreground mt-0.5 shrink-0" />
                    <p>{receipt.checkin_location}</p>
                  </div>
                </CardContent>
              </Card>
            )}

            <Card className="mb-6 border-primary/20 bg-primary/5">
              <CardContent className="pt-6">
                <h2 className="text-lg font-semibold mb-4 text-primary">What's Next?</h2>
                <div className="text-sm space-y-4">
                  <p>
                    My name is Michael. I am the Director of Media for RecruitLook Hoops. I would like to thank you for applying to cover our upcoming event. Below is our logo to use for all your media publications.
                  </p>

                  <div className="flex justify-center py-4">
                    <img
                      src={recruitlookHoopsLogo}
                      alt="RecruitLook Hoops Logo"
                      className="h-24 object-contain"
                    />
                  </div>

                  <p>
                    {receipt.is_approved ? (
                      <>Your media credential for <strong>{event.name} ({formatEventDates()})</strong> is approved. Please make sure you follow our{" "}</>
                    ) : (
                      <>Your media request for <strong>{event.name} ({formatEventDates()})</strong> has been received and is under review. Once approved, please make sure you follow our{" "}</>
                    )}

                    <a
                      href="https://recruitlook.com/media-policy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary underline hover:text-primary/80"
                    >
                      media guidelines (Click Here)
                    </a>. Failure to follow our guidelines will jeopardize your good standing with us.
                  </p>

                  <div>
                    <p className="font-semibold mb-2">Check-in Information:</p>
                    <ul className="space-y-2 ml-4">
                      <li>• Have your email receipt ready to show.</li>
                      <li>• Bring your ID to show proof.</li>
                      <li>• Have proof of your business to show (business card, social media logged in, etc).</li>
                      <li>• You will receive 1 wristband for the entire weekend. If you lose your wristband, you'll need to reapply and pay for new media credentials.</li>
                      <li>• Check in at the Tournament Headquarters listed on the website.</li>
                      <li>• All media members will check in at the Team Check-in area.</li>
                    </ul>
                  </div>

                  <p>Please contact us with any questions.</p>

                  <div className="pt-2">
                    <p>Thanks,</p>
                    <p className="font-semibold">Michael Sanders</p>
                    <p>Director of Media</p>
                    <p>RecruitLook Hoops</p>
                    <a
                      href="mailto:recruitlookmedia@gmail.com"
                      className="text-primary underline hover:text-primary/80"
                    >
                      recruitlookmedia@gmail.com
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button variant="outline" onClick={() => navigate('/events')}>
                Browse More Events
              </Button>
              <Button onClick={() => navigate(`/events/${event.slug || event.id}`)}>
                View Event Details
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-background py-8 px-4">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-4">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-foreground mb-2">Request Submitted!</h1>
            <p className="text-muted-foreground">
              Your media credentials request has been submitted. You will receive a confirmation email shortly.
            </p>
          </div>

          <Card className="mb-6">
            <CardContent className="pt-6">
              <h2 className="text-lg font-semibold mb-4 text-primary">Event Information</h2>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <Calendar className="h-5 w-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="font-medium">{event.name}</p>
                    <p className="text-sm text-muted-foreground">{formatEventDates()}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <MapPin className="h-5 w-5 text-muted-foreground mt-0.5" />
                  <p className="text-sm">{getVenueLocation()}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {submittedRequest && (
            <Card className="mb-6">
              <CardContent className="pt-6">
                <h2 className="text-lg font-semibold mb-4 text-primary">What You Requested</h2>
                <dl className="space-y-2 text-sm">
                  {[
                    ['Name', submittedRequest.name],
                    ['Outlet', submittedRequest.organization],
                    ['Email', submittedRequest.email],
                    ['Phone', submittedRequest.phone ? formatUsPhone(submittedRequest.phone) : ''],
                    ['Coverage type', submittedRequest.coverage_type],
                  ]
                    .filter(([, v]) => !!v)
                    .map(([label, value]) => (
                      <div key={String(label)} className="flex items-center justify-between gap-4">
                        <dt className="text-muted-foreground">{label}</dt>
                        <dd className="font-medium text-right">{String(value)}</dd>
                      </div>
                    ))}
                  <div className="flex items-center justify-between gap-4">
                    <dt className="text-muted-foreground">Status</dt>
                    <dd className="font-medium">Pending review</dd>
                  </div>
                </dl>
                <p className="text-sm text-muted-foreground mt-4">
                  A confirmation email is on its way to {submittedRequest.email || 'the address you provided'}. The
                  event's media team reviews requests and typically responds within 3–5 business days, and always
                  before the event starts. You'll get an email either way.
                </p>
              </CardContent>
            </Card>
          )}

          {paymentStatus && (
            <Card className="mb-6">
              <CardContent className="pt-6">
                <h2 className="text-lg font-semibold mb-4 text-primary">Payment Information</h2>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between gap-4">
                    <span className="text-muted-foreground">Status</span>
                    <span className="font-medium capitalize">{paymentStatus.replace('_', ' ')}</span>
                  </div>
                  {paymentAmountCents > 0 && (
                    <div className="flex items-center justify-between gap-4">
                      <span className="text-muted-foreground">Amount</span>
                      <span className="font-medium">${(paymentAmountCents / 100).toFixed(2)}</span>
                    </div>
                  )}
                  {paymentCoupon && (
                    <div className="flex items-center justify-between gap-4">
                      <span className="text-muted-foreground">Coupon</span>
                      <span className="font-medium">{paymentCoupon}</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {receiptError && (
            <Card className="mb-6 border-l-4 border-l-muted bg-muted/10">
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground">{receiptError}</p>
              </CardContent>
            </Card>
          )}

          <Card className="mb-6 border-primary/20 bg-primary/5">
            <CardContent className="pt-6">
              <h2 className="text-lg font-semibold mb-4 text-primary">What's Next?</h2>
              <div className="text-sm space-y-4">
                <p>
                  My name is Michael. I am the Director of Media for RecruitLook Hoops. I would like to thank you for applying to cover our upcoming event. Below is our logo to use for all your media publications.
                </p>

                <div className="flex justify-center py-4">
                  <img
                    src={recruitlookHoopsLogo}
                    alt="RecruitLook Hoops Logo"
                    className="h-24 object-contain"
                  />
                </div>

                <p>
                  Your media request for <strong>{event.name} ({formatEventDates()})</strong> has been received and is
                  under review. Once approved, please make sure you follow our{" "}

                  <a
                    href="https://recruitlook.com/media-policy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary underline hover:text-primary/80"
                  >
                    media guidelines (Click Here)
                  </a>. Failure to follow our guidelines will jeopardize your good standing with us.
                </p>

                <div>
                  <p className="font-semibold mb-2">Check-in Information:</p>
                  <ul className="space-y-2 ml-4">
                    <li>• Have your email receipt ready to show.</li>
                    <li>• Bring your ID to show proof.</li>
                    <li>• Have proof of your business to show (business card, social media logged in, etc).</li>
                    <li>• You will receive 1 wristband for the entire weekend. If you lose your wristband, you'll need to reapply and pay for new media credentials.</li>
                    <li>• Check in at the Tournament Headquarters listed on the website.</li>
                    <li>• All media members will check in at the Team Check-in area.</li>
                  </ul>
                </div>

                <p>Please contact us with any questions.</p>

                <div className="pt-2">
                  <p>Thanks,</p>
                  <p className="font-semibold">Michael Sanders</p>
                  <p>Director of Media</p>
                  <p>RecruitLook Hoops</p>
                  <a
                    href="mailto:recruitlookmedia@gmail.com"
                    className="text-primary underline hover:text-primary/80"
                  >
                    recruitlookmedia@gmail.com
                  </a>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button variant="outline" onClick={() => navigate('/events')}>
              Browse More Events
            </Button>
            <Button onClick={() => navigate(`/events/${event.slug || event.id}`)}>
              View Event Details
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Card payments carry the same 3% processing fee as every other RLE checkout.
  const cardFee = Math.round(discountedPrice * 0.03 * 100) / 100;
  const totalDue = Math.round((discountedPrice + cardFee) * 100) / 100;
  const priceDisplay = discountedPrice > 0 ? `$${totalDue.toFixed(2)}` : 'Free';

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-br from-slate-900 to-gray-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/80 to-gray-900/80" />
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Badge className="bg-secondary text-black px-6 py-2 font-black mb-4">
            <Camera className="w-4 h-4 mr-2" />
            MEDIA
          </Badge>
          <h1 className="text-3xl lg:text-5xl font-black mb-2">Media Credentials</h1>
          <p className="text-xl text-gray-300 mb-4">{event.name}</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 text-sm text-gray-300">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              <span>{formatEventDates()}</span>
            </div>
            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4" />
              <span>{getVenueLocation()}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-5 gap-8">
          {/* Description / Info Column */}
          <div className="lg:col-span-2">
            {event.media_credentials_description && (
              <Card className="mb-6">
                <CardContent className="pt-6">
                  <h3 className="font-semibold mb-4">Media Credential Information</h3>
                  <div 
                    className="prose prose-sm max-w-none dark:prose-invert 
                      [&_p]:mb-3 [&_p]:leading-relaxed
                      [&_ul]:list-disc [&_ul]:ml-6 [&_ul]:mb-3 [&_ul]:space-y-2
                      [&_ol]:list-decimal [&_ol]:ml-6 [&_ol]:mb-3 [&_ol]:space-y-2
                      [&_li]:mb-1 [&_li]:leading-relaxed
                      [&_strong]:font-bold [&_b]:font-bold"
                    dangerouslySetInnerHTML={{ __html: event.media_credentials_description }}
                  />
                </CardContent>
              </Card>
            )}

            {(event.media_credentials_price ?? 0) > 0 && (
              <Card className="mb-6 border-secondary/50 bg-secondary/5">
                <CardContent className="pt-6">
                  <p className="text-sm text-muted-foreground mb-3 text-center">Credential Fee</p>
                  {couponApplied ? (
                    <p className="text-2xl font-bold text-green-600 text-center">Free</p>
                  ) : (
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span>Media credential</span>
                        <span>${discountedPrice.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-muted-foreground">
                        <span>Card processing fee (3%)</span>
                        <span>${cardFee.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between border-t pt-2 text-base font-bold text-primary">
                        <span>Total</span>
                        <span>${totalDue.toFixed(2)}</span>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>


          {/* Form Column */}
          <div className="lg:col-span-3">
            <Card>
              <CardContent className="pt-6">
                <h2 className="text-xl font-bold mb-6">Request Media Credentials</h2>

                {formFields.length > 0 ? (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    {formFields.map((field) => (
                      <div key={field.id}>
                        <Label 
                          htmlFor={field.field_name}
                          className="block text-sm font-medium mb-1"
                        >
                          {field.field_label}
                          {field.is_required && <span className="text-red-500 ml-1">*</span>}
                        </Label>
                        {renderFormField(field)}
                      </div>
                    ))}

                    {/* Coupon Code Section */}
                    {(event.media_credentials_price ?? 0) > 0 && !couponApplied && (
                      <div className="pt-4 border-t">
                        <Label className="block text-sm font-medium mb-2">Have a coupon code?</Label>
                        <div className="flex gap-2">
                          <div className="relative flex-1">
                            <Tag className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                              type="text"
                              value={couponCode}
                              onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                              placeholder="Enter code"
                              className="pl-9"
                            />
                          </div>
                          <Button type="button" variant="outline" onClick={handleApplyCoupon}>
                            Apply
                          </Button>
                        </div>
                      </div>
                    )}
                    
                    <div className="pt-6">
                      <Button 
                        type="submit" 
                        className="w-full py-6 text-lg font-bold"
                        disabled={submitting}
                      >
                        {submitting ? (
                          <>
                            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                            Processing...
                          </>
                        ) : discountedPrice > 0 ? (
                          `Submit & Pay ${priceDisplay}`
                        ) : (
                          'Submit Request'
                        )}
                      </Button>
                    </div>
                  </form>
                ) : (() => {
                  const enabled =
                    event.media_credentials_enabled ?? event.enable_media_credentials ?? false;
                  if (!enabled) {
                    return (
                      <div className="text-center py-8 space-y-3">
                        <h3 className="text-lg font-semibold">
                          Media credentials not available for this event
                        </h3>
                        <p className="text-sm text-muted-foreground max-w-md mx-auto">
                          This event doesn't currently accept media credential
                          applications. Contact the event organizer if you have
                          questions.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-2 justify-center pt-2">
                          {event.media_credentials_notification_email && (
                            <Button asChild variant="outline">
                              <a
                                href={`mailto:${event.media_credentials_notification_email}?subject=Media credentials for ${encodeURIComponent(
                                  event.name,
                                )}`}
                              >
                                Contact organizer
                              </a>
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            onClick={() => navigate(`/events/${event.slug || event.id}`)}
                          >
                            Back to Event
                          </Button>
                        </div>
                      </div>
                    );
                  }
                  // Enabled but no form configured yet (admin oversight)
                  console.warn(
                    `[media-credentials] Event ${event.id} has media credentials enabled but no form configured.`,
                  );
                  return (
                    <div className="text-center py-8 space-y-3">
                      <h3 className="text-lg font-semibold">
                        Application form is being prepared
                      </h3>
                      <p className="text-sm text-muted-foreground max-w-md mx-auto">
                        Media credentials are enabled for this event, but the
                        application form hasn't been published yet. Please check
                        back soon.
                      </p>
                      <Button
                        variant="outline"
                        className="mt-2"
                        onClick={() => navigate(`/events/${event.slug || event.id}`)}
                      >
                        Back to Event
                      </Button>
                    </div>
                  );
                })()}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Back Button */}
        <div className="mt-8 text-center">
          <Button 
            variant="ghost" 
            onClick={() => navigate(`/events/${event.slug || event.id}`)}
          >
            ← Back to Event Details
          </Button>
        </div>
      </div>
    </div>
  );
};

export default EventMediaCredentialsPage;